/**
 * TenantAdequacyToggleUI.tsx
 * BUILD-TENANT-ADEQUACY-TOGGLE — Tenant Admin UI
 *
 * Locked decisions:
 *   S-CP10-3-A  (Fri May 15 2026 2:06 PM EDT, Bryan Short)
 *   SATURDAY_BUILD_PLAN.md §APPENDIX C
 *   Yvonne review required on admin UI design.
 *
 * Admin-only screen component. Renders a toggle for the tenant's
 * push_adequacy_mode setting. Saving writes to public.tenants.
 *
 * UI-12 reads this setting at render time to branch between:
 *   rep_self_check          → checkbox flow
 *   manager_reads_and_judges → SOFT_STOP flag + Adequate / Inadequate buttons
 */

import React, { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';

// ─── Types ────────────────────────────────────────────────────────────────────

type PushAdequacyMode = 'rep_self_check' | 'manager_reads_and_judges';

type TenantAdequacyConfig = {
  tenant_id: number;
  tenant_name: string;
  push_adequacy_mode: PushAdequacyMode;
};

// ─── Constants ────────────────────────────────────────────────────────────────

const MODE_LABELS: Record<PushAdequacyMode, { label: string; description: string }> = {
  rep_self_check: {
    label: 'Rep self-check',
    description:
      'Rep confirms adequacy by checking a checkbox ("Yes, I have materially new info"). ' +
      'Manager is alerted as an attention flag but does not gate the submission.',
  },
  manager_reads_and_judges: {
    label: 'Manager reads and judges',
    description:
      'Manager receives a review item with the rep\'s full free-text answer. ' +
      'Manager must click "Adequate" or "Inadequate" before the deal can proceed. ' +
      'One retry permitted within 48 hours; then deal auto-converts to LOSS.',
  },
};

// ─── Component ────────────────────────────────────────────────────────────────

interface TenantAdequacyToggleProps {
  supabase: any;
  tenantId: number;
  /** If true, renders in read-only mode (non-admin viewer) */
  readOnly?: boolean;
}

export const TenantAdequacyToggle: React.FC<TenantAdequacyToggleProps> = ({
  supabase,
  tenantId,
  readOnly = false,
}) => {
  const [config, setConfig] = useState<TenantAdequacyConfig | null>(null);
  const [pending, setPending] = useState<PushAdequacyMode | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [loading, setLoading] = useState(true);

  // Load current setting
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      const { data, error } = await supabase
        .from('tenants')
        .select('id, name, push_adequacy_mode')
        .eq('id', tenantId)
        .single();

      if (!cancelled) {
        if (error || !data) {
          setSaveError('Failed to load tenant configuration.');
        } else {
          setConfig({
            tenant_id: data.id,
            tenant_name: data.name,
            push_adequacy_mode: data.push_adequacy_mode,
          });
        }
        setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [tenantId]);

  const currentMode = pending ?? config?.push_adequacy_mode ?? 'rep_self_check';
  const isDirty = pending !== null && pending !== config?.push_adequacy_mode;

  async function handleSave() {
    if (!pending || !isDirty) return;
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    const { error } = await supabase
      .from('tenants')
      .update({ push_adequacy_mode: pending })
      .eq('id', tenantId);

    if (error) {
      setSaveError(`Failed to save: ${error.message}`);
    } else {
      setConfig((prev) => prev ? { ...prev, push_adequacy_mode: pending } : prev);
      setPending(null);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    }
    setSaving(false);
  }

  function handleDiscard() {
    setPending(null);
    setSaveError(null);
  }

  if (loading) {
    return (
      <div className="p-4 text-sm text-gray-500">
        Loading adequacy mode configuration…
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm max-w-2xl">
      {/* Header */}
      <div className="mb-5">
        <h2 className="text-base font-semibold text-gray-900">
          PUSH Defense Adequacy Check Mode
        </h2>
        <p className="mt-1 text-sm text-gray-500">
          Controls how CP10 PUSH defense answers are judged for adequacy.
          Setting applies to all deals in this tenant.
          {config && (
            <span className="ml-1 font-medium text-gray-700">
              Tenant: {config.tenant_name}
            </span>
          )}
        </p>
      </div>

      {/* Toggle options */}
      <fieldset disabled={readOnly || saving} className="space-y-3">
        {(Object.keys(MODE_LABELS) as PushAdequacyMode[]).map((mode) => {
          const { label, description } = MODE_LABELS[mode];
          const isSelected = currentMode === mode;
          return (
            <label
              key={mode}
              className={[
                'flex cursor-pointer items-start gap-4 rounded-lg border p-4 transition-colors',
                isSelected
                  ? 'border-indigo-600 bg-indigo-50'
                  : 'border-gray-200 hover:border-gray-300',
                readOnly ? 'cursor-default' : '',
              ].join(' ')}
            >
              <input
                type="radio"
                name="push_adequacy_mode"
                value={mode}
                checked={isSelected}
                onChange={() => !readOnly && setPending(mode)}
                className="mt-0.5 h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
              />
              <div className="flex-1 min-w-0">
                <span className="block text-sm font-medium text-gray-900">{label}</span>
                <span className="mt-0.5 block text-sm text-gray-500">{description}</span>
              </div>
            </label>
          );
        })}
      </fieldset>

      {/* Action row */}
      {!readOnly && (
        <div className="mt-5 flex items-center gap-3">
          <button
            type="button"
            disabled={!isDirty || saving}
            onClick={handleSave}
            className={[
              'inline-flex items-center rounded-md px-4 py-2 text-sm font-semibold shadow-sm',
              isDirty && !saving
                ? 'bg-indigo-600 text-white hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed',
            ].join(' ')}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>

          {isDirty && !saving && (
            <button
              type="button"
              onClick={handleDiscard}
              className="text-sm text-gray-500 hover:text-gray-700 underline"
            >
              Discard
            </button>
          )}

          {saveSuccess && (
            <span className="text-sm text-green-600 font-medium">
              Saved. Change takes effect on next deal evaluation.
            </span>
          )}

          {saveError && (
            <span className="text-sm text-red-600">{saveError}</span>
          )}
        </div>
      )}

      {/* Info footer */}
      <p className="mt-4 text-xs text-gray-400">
        Default for all tenants: Rep self-check.
        Partner default: Manager reads and judges (locked Fri May 15 2026 per Bryan Short).
      </p>
    </div>
  );
};

export default TenantAdequacyToggle;
